"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

type PanelId = "panel1" | "panel2" | "panel3";

export interface OvermoldingFinishesAccordionProps {
	translationNamespace?: string;
	/** 默认展开的面板 id，默认第一项 */
	defaultOpenId?: PanelId | null;
}

/**
 * JS Precision's Overmolding Finishes：居中标题 + 绿装饰线；灰边框手风琴，单开模式，Chevron 指示展开
 */
export default function OvermoldingFinishesAccordion({
	translationNamespace = "overmoldingFinishesSection",
	defaultOpenId = "panel1",
}: OvermoldingFinishesAccordionProps) {
	const t = useTranslations(translationNamespace);
	const [openId, setOpenId] = useState<PanelId | null>(defaultOpenId);

	const toggle = (id: PanelId) => {
		setOpenId((prev) => (prev === id ? null : id));
	};

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 flex flex-wrap items-end justify-center gap-3 sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-center text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<div
						className="h-px w-28 shrink-0 rounded-full bg-themePrimary400 sm:h-[2px] sm:w-36 md:w-44"
						aria-hidden
					/>
				</div>

				<div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
					{/* Panel 1：SPI Surface Finishes */}
					<div className="border-b border-gray-200 last:border-b-0">
						<button
							type="button"
							onClick={() => toggle("panel1")}
							className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors duration-200 hover:bg-gray-50 sm:px-6 sm:py-5"
							aria-expanded={openId === "panel1"}
						>
							<span className="text-base font-bold text-black sm:text-lg">{t("panel1Title")}</span>
							<span className="flex h-8 w-8 shrink-0 items-center justify-center text-themePrimary400">
								{openId === "panel1" ? (
									<CaretDown className="h-5 w-5" weight="bold" aria-hidden />
								) : (
									<CaretRight className="h-5 w-5" weight="bold" aria-hidden />
								)}
							</span>
						</button>
						<div
							className={`grid transition-all duration-300 ease-in-out ${
								openId === "panel1" ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
							}`}
						>
							<div className="min-h-0 overflow-hidden">
								<div className="px-4 pb-5 pt-0 text-sm leading-relaxed text-black sm:px-6 sm:pb-6 md:text-base">
									{t("panel1Desc")}
								</div>
							</div>
						</div>
					</div>

					{/* Panel 2：Available Finishes */}
					<div className="border-b border-gray-200 last:border-b-0">
						<button
							type="button"
							onClick={() => toggle("panel2")}
							className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors duration-200 hover:bg-gray-50 sm:px-6 sm:py-5"
							aria-expanded={openId === "panel2"}
						>
							<span className="text-base font-bold text-black sm:text-lg">{t("panel2Title")}</span>
							<span className="flex h-8 w-8 shrink-0 items-center justify-center text-themePrimary400">
								{openId === "panel2" ? (
									<CaretDown className="h-5 w-5" weight="bold" aria-hidden />
								) : (
									<CaretRight className="h-5 w-5" weight="bold" aria-hidden />
								)}
							</span>
						</button>
						<div
							className={`grid transition-all duration-300 ease-in-out ${
								openId === "panel2" ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
							}`}
						>
							<div className="min-h-0 overflow-hidden">
								<div className="space-y-3 px-4 pb-5 pt-0 text-sm leading-relaxed text-black sm:px-6 sm:pb-6 md:text-base">
									<p className="font-semibold text-black">{t("panel2Lead")}</p>
									<ul className="list-none space-y-1.5 pl-0">
										<li>{t("panel2Row1")}</li>
										<li>{t("panel2Row2")}</li>
										<li>{t("panel2Row3")}</li>
										<li>{t("panel2Row4")}</li>
									</ul>
								</div>
							</div>
						</div>
					</div>

					{/* Panel 3：As Molded */}
					<div className="border-b border-gray-200 last:border-b-0">
						<button
							type="button"
							onClick={() => toggle("panel3")}
							className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition-colors duration-200 hover:bg-gray-50 sm:px-6 sm:py-5"
							aria-expanded={openId === "panel3"}
						>
							<span className="text-base font-bold text-black sm:text-lg">{t("panel3Title")}</span>
							<span className="flex h-8 w-8 shrink-0 items-center justify-center text-themePrimary400">
								{openId === "panel3" ? (
									<CaretDown className="h-5 w-5" weight="bold" aria-hidden />
								) : (
									<CaretRight className="h-5 w-5" weight="bold" aria-hidden />
								)}
							</span>
						</button>
						<div
							className={`grid transition-all duration-300 ease-in-out ${
								openId === "panel3" ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
							}`}
						>
							<div className="min-h-0 overflow-hidden">
								<div className="px-4 pb-5 pt-0 text-sm leading-relaxed text-black sm:px-6 sm:pb-6 md:text-base">
									{t("panel3Desc")}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
